import { useState, useEffect, useCallback, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { C, cs } from '../styles/common';
import Checklist from '../components/Checklist';

// ── Info tooltips ─────────────────────────────────────────────
const INFO = {
  zones:         'Barrel zone temperatures calculated using a progressive step-down profile from front (nozzle) to feed zone. Front = target melt − shear heat. Each zone steps down 10–15°C.',
  mould_temp:    'Mould temperature from the material datasheet. Controls surface quality, shrinkage and cycle time.',
  melt_temp:     "Estimated melt temperature = front zone + shear heat. Must fall within the material's processing window.",
  fill_time:     'Fill time based on shot volume and screw area at 70% of max injection speed.',
  inj_pressure:  'Maximum injection pressure from flow length, viscosity (MFI) and mould geometry. Machine limit as ceiling.',
  inj_stages:    'Multi-stage injection. S1: fast fill. S2: slow approach to V→P. S3: decelerate. Speeds are % of max machine speed.',
  vp:            'V→P switchover — where machine transitions from velocity to pressure control.',
  shear_rate:    "Shear rate at the gate. Must stay below material's max shear rate to avoid degradation or splay.",
  gate_freeze:   'Time for the gate to solidify. Hold time must exceed this to prevent backflow.',
  hold_stages:   'Hold pressure profile. H1: high pressure to pack shrinkage, steps down progressively.',
  suckback:      'Screw retract after recovery to reduce nozzle drool. Higher MFI needs more suckback.',
  cooling_min:   "Minimum cooling time from Fourier's law. Uses wall thickness, thermal diffusivity, melt and ejection temp.",
  cooling_rec:   'Recommended cooling time = minimum × 1.20. Start here and reduce by 0.5s when stable.',
  flow_rate:     'Cooling water flow rate per circuit to remove heat within cooling time. Turbulent flow (Re > 4000) recommended.',
  screw_speed:   'Screw speed = midpoint of machine × material overlap. Higher MFI tolerates higher speed.',
  back_pressure: 'Back pressure = midpoint of machine × material overlap. Higher = better homogeneity, longer recovery.',
  recovery:      'Time for screw to recover next shot. Must be less than cooling time.',
  shot_barrel:   'Shot volume as % of barrel. Ideal 20–80%. Below 20%: long residence risk. Above 80%: inconsistent shot risk.',
  residence:     'Time in barrel at melt temperature. Must stay below 360s to prevent thermal degradation.',
  clamp:         'Minimum clamp force = projected area × cavity pressure × cavities. Suggested = minimum × 1.10.',
  closing:       'Mould closing. Fast close → slow approach → high-pressure lock.',
  opening:       'Mould opening. Slow break to overcome adhesion, fast open, decelerate.',
  ejection:      'Ejection. Delay allows part to shrink onto core. Speed based on MFI.',
  shot_vol:      'Shot volume = shot weight ÷ melt density (cm³).',
  dosing_stroke: 'Dosing stroke = shot volume ÷ screw area. How far screw travels back during recovery.',
  clamp_sugg:    'Suggested clamp force = minimum × 1.10 safety factor.',
  ej_stroke:     'Ejector stroke from mould record. Must fully clear the part from the core.',
  ej_fwd:        'Ejector forward speed based on MFI — higher flow = more flexible parts, faster ejection.',
  ej_ret:        'Ejector retract speed — typically 30–40% faster than forward (unloaded).',
  ej_pulses:     'Number of ejector pulses. Standard is 1. Increase to 2–3 if parts stick.',
};

// ── Safety check details ──────────────────────────────────────
const CHECK_DETAILS = {
  melt_in_window:      { severity: 'amber', label: 'Melt in window',        fix: 'Adjust barrel zone temperatures. Increase front zone if melt is too cold, decrease if too hot.' },
  residence_time:      { severity: 'red',   label: 'Residence time',        fix: 'Residence time too long — thermal degradation risk. Use smaller barrel, increase shot weight, or reduce cycle time.' },
  gate_shear_rate:     { severity: 'red',   label: 'Gate shear rate',       fix: 'Gate shear rate too high — risk of splay, burning or degradation. Increase gate dimensions or reduce injection speed.' },
  clamp_force:         { severity: 'red',   label: 'Clamp force',           fix: 'Required clamp force exceeds machine capacity. Use larger tonnage machine or reduce injection pressure.' },
  hold_vs_gate_freeze: { severity: 'amber', label: 'Hold vs gate freeze',   fix: 'Hold time less than gate freeze — backflow may occur. Increase total hold time or reduce gate depth.' },
  recovery_vs_cooling: { severity: 'amber', label: 'Recovery vs cooling',   fix: 'Recovery exceeds cooling time — cycle extended. Increase screw speed or reduce shot weight.' },
  shot_barrel_ratio:   { severity: 'amber', label: 'Shot / barrel ratio',   fix: 'Shot/barrel ratio outside 20–80%. Below 20%: smaller barrel. Above 80%: larger barrel.' },
  cooling_turbulence:          { severity: 'amber', label: 'Cooling flow regime',          fix: 'Coolant flow is transitional or laminar (Re < 4,000). Turbulent flow transfers heat 3–5× more efficiently. First step: increase your coolant pump flow rate or pressure. If already at maximum: reduce circuit diameter (smaller bore → higher velocity), or split long circuits into shorter parallel runs.' },
  mould_fits_machine:          { severity: 'amber', label: 'Mould fits machine',            fix: 'Mould dimensions exceed machine capacity. Check stack height against machine daylight range and mould width/length against tie bar spacing.' },
  chiller_capacity:            { severity: 'amber', label: 'Chiller capacity',              fix: 'Heat load exceeds chiller cooling capacity — the mould will heat up progressively through the run, causing dimensional drift and longer cycle times. Increase chiller capacity, reduce cycle rate, or add a secondary cooling circuit.' },
  chiller_achieves_mould_temp: { severity: 'amber', label: 'Chiller temp vs mould target',  fix: 'Chiller minimum temperature is too close to or above the target mould temperature. Allow for circuit ΔT and film resistance — chiller setpoint should be at least 3–5°C below the target mould wall temperature. Use a lower-temperature chiller or reduce mould temp target if the material allows.' },
  cooling_flow_supply:         { severity: 'amber', label: 'Coolant flow supply',           fix: 'Required flow rate exceeds chiller maximum. Reduce circuit resistance (larger bore, shorter runs, parallel circuits) or upgrade the chiller/pump.' },
  cooling_pressure_supply:     { severity: 'amber', label: 'Coolant pressure supply',       fix: 'Circuit pressure drop exceeds chiller maximum supply pressure. Reduce bore length, increase bore diameter, add parallel circuits, or use a higher-pressure pump.' },
  condensation_risk:           { severity: 'amber', label: 'Condensation risk',             fix: 'Mould surface temperature is at or below the workshop dew point — moisture will condense on the mould causing surface defects, sink marks, and contamination. Raise mould temperature, reduce workshop humidity with a dehumidifier, or enclose and heat the mould area.' },
};

// ── Edit-mode compact sections ────────────────────────────────
const SECTIONS = [
  { key: 'temperatures', label: 'Temperatures', params: [
    { path: 'zones[0].temp', label: 'Z1 Feed',    unit: '°C' },
    { path: 'zones[1].temp', label: 'Z2',          unit: '°C' },
    { path: 'zones[2].temp', label: 'Z3',          unit: '°C' },
    { path: 'zones[3].temp', label: 'Z4 / Nozzle', unit: '°C' },
    { path: 'mould_temp',    label: 'Mould temp',  unit: '°C' },
  ]},
  { key: 'injection', label: 'Injection', params: [
    { path: 'fill_time_s',                label: 'Fill time',    unit: 's'   },
    { path: 'inj_pressure_bar',           label: 'Inj pressure', unit: 'bar' },
    { path: 'inj_stages[0].speed_pct',    label: 'S1 speed',     unit: '%'   },
    { path: 'inj_stages[0].pressure_bar', label: 'S1 pressure',  unit: 'bar' },
    { path: 'inj_stages[1].speed_pct',    label: 'S2 speed',     unit: '%'   },
    { path: 'inj_stages[1].pressure_bar', label: 'S2 pressure',  unit: 'bar' },
    { path: 'inj_stages[2].speed_pct',    label: 'S3 speed',     unit: '%'   },
    { path: 'inj_stages[2].pressure_bar', label: 'S3 pressure',  unit: 'bar' },
    { path: 'vp_value',                   label: 'V→P',          unit: ''    },
  ]},
  { key: 'hold', label: 'Hold / Pack', params: [
    { path: 'hold_stages[0].pressure_bar', label: 'H1 pressure', unit: 'bar' },
    { path: 'hold_stages[0].time_s',       label: 'H1 time',     unit: 's'   },
    { path: 'hold_stages[1].pressure_bar', label: 'H2 pressure', unit: 'bar' },
    { path: 'hold_stages[1].time_s',       label: 'H2 time',     unit: 's'   },
    { path: 'hold_stages[2].pressure_bar', label: 'H3 pressure', unit: 'bar' },
    { path: 'hold_stages[2].time_s',       label: 'H3 time',     unit: 's'   },
    { path: 'suckback_mm',                 label: 'Suckback',    unit: 'mm'  },
  ]},
  { key: 'cooling', label: 'Cooling', params: [
    { path: 'cooling_rec_s',    label: 'Cooling time', unit: 's'     },
    { path: 'flow_rate_l_min',  label: 'Flow rate',    unit: 'l/min' },
  ]},
  { key: 'plasticising', label: 'Plasticising', params: [
    { path: 'screw_speed_rpm',   label: 'Screw speed',   unit: 'rpm' },
    { path: 'back_pressure_bar', label: 'Back pressure', unit: 'bar' },
  ]},
];

const OUTCOME_LABELS = {
  adjusted: { label: 'Adjusted', bg: '#1e3a5f', color: '#93c5fd' },
  stable:   { label: 'Stable',   bg: '#14532d', color: '#86efac' },
  golden:   { label: 'Golden ⭐', bg: '#451a03', color: '#fde68a' },
  issue:    { label: 'Issue',    bg: '#450a0a', color: '#fca5a5' },
};
const TYPE_ICONS = { calculated: '🔬', edit: '✏️', troubleshoot: '🔍', golden: '⭐', suggest_fixes: '🔧', optimise: '✨' };

// ── Parameter groups for troubleshoot mode ─────────────────────
const PARAM_GROUPS = [
  ['zones[0].temp','zones[1].temp','zones[2].temp','zones[3].temp'],
  ['inj_stages[0].speed_pct','inj_stages[0].pressure_bar','inj_stages[1].speed_pct','inj_stages[1].pressure_bar','inj_stages[2].speed_pct','inj_stages[2].pressure_bar'],
  ['hold_stages[0].pressure_bar','hold_stages[0].time_s','hold_stages[1].pressure_bar','hold_stages[1].time_s','hold_stages[2].pressure_bar','hold_stages[2].time_s'],
];

function countChangedGroups(paths) {
  const hitGroups = new Set();
  let individual = 0;
  paths.forEach(path => {
    const gIdx = PARAM_GROUPS.findIndex(g => g.includes(path));
    if (gIdx >= 0) hitGroups.add(gIdx);
    else individual++;
  });
  return hitGroups.size + individual;
}

function getLockedPaths(changedPaths) {
  if (!changedPaths.length) return new Set();
  const firstPath = changedPaths[0];
  const gIdx = PARAM_GROUPS.findIndex(g => g.includes(firstPath));
  const allowed = gIdx >= 0 ? new Set(PARAM_GROUPS[gIdx]) : new Set([firstPath]);
  const locked = new Set();
  SECTIONS.forEach(sec => sec.params.forEach(p => {
    if (!allowed.has(p.path)) locked.add(p.path);
  }));
  return locked;
}

// ── Path helpers ──────────────────────────────────────────────
function getPath(obj, path) {
  if (!obj) return undefined;
  return path.split(/[.[\]]+/).filter(Boolean).reduce((o, k) => {
    if (o == null) return undefined;
    return o[isNaN(k) ? k : parseInt(k)];
  }, obj);
}
function setPath(obj, path, value) {
  const clone = JSON.parse(JSON.stringify(obj));
  const parts = path.split(/[.[\]]+/).filter(Boolean);
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = isNaN(parts[i]) ? parts[i] : parseInt(parts[i]);
    cur = cur[k];
  }
  const last = parts[parts.length - 1];
  cur[isNaN(last) ? last : parseInt(last)] = value;
  return clone;
}
function applyOverrides(base, overrides) {
  let result = JSON.parse(JSON.stringify(base));
  Object.entries(overrides).forEach(([path, val]) => {
    const num = parseFloat(val);
    result = setPath(result, path, isNaN(num) ? val : num);
  });
  return result;
}

// ── Main component ────────────────────────────────────────────
export default function Optimisation() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { user, can } = useAuth();
  const canEdit = can('edit_process');
  const { toast }    = useToast();

  const [opt, setOpt]               = useState(null);
  const [versions, setVersions]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editMode, setEditMode]     = useState(null);
  const [editValues, setEditValues] = useState({});
  const [editReason, setEditReason] = useState('');
  const [editOutcome, setEditOutcome] = useState('');
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [markingGolden, setMarkingGolden]   = useState(false);
  const [viewMode, setViewMode]     = useState('full');
  const [currentStep, setCurrentStep] = useState(0);
  const [showChecklist, setShowChecklist] = useState(null);
  const [fontKey, setFontKey]       = useState('normal');
  const [expandedCheck, setExpandedCheck] = useState(null);
  const [showSuggest, setShowSuggest]       = useState(false);
  const [suggestSel, setSuggestSel]         = useState([]);
  const [suggestApplying, setSuggestApplying] = useState(false);
  const [suggestions, setSuggestions]       = useState([]);
  const [optimiseResult, setOptimiseResult] = useState(null); // null=calculating, []=none, [...]= ready
  const [showOptimise, setShowOptimise]     = useState(false);
  const [optimiseSel, setOptimiseSel]       = useState([]);
  const [optimiseApplying, setOptimiseApplying] = useState(false);

  const fontMult = fontKey === 'xl' ? 1.5 : fontKey === 'large' ? 1.25 : 1;

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/v1/optimisations/${id}`)
      .then(r => { setOpt(r.data); setVersions(r.data.versions || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { document.body.style.zoom = '100%'; }, []);

  // Derived
  const v1       = versions.find(v => v.version_number === 1);
  const goldenV  = versions.find(v => v.is_golden);
  const latestV  = versions[versions.length - 1];
  const refV     = goldenV || v1;
  const currentV = viewingVersion ? versions.find(v => v.id === viewingVersion) : latestV;
  const params   = currentV?.params;

  const baseParams   = currentV?.params;
  const changedPaths = editMode && baseParams
    ? Object.entries(editValues)
        .filter(([path, val]) => {
          const orig = getPath(baseParams, path);
          // String equality first — handles non-numeric values like vp_value
          if (String(orig ?? '') === String(val ?? '')) return false;
          // Numeric equality — handles float precision ("1.50" vs 1.5)
          const oNum = parseFloat(orig);
          const vNum = parseFloat(val);
          if (!isNaN(oNum) && !isNaN(vNum)) return oNum !== vNum;
          return true;
        })
        .map(([path]) => path)
    : [];
  const changedGroups = editMode === 'troubleshoot' ? countChangedGroups(changedPaths) : 0;
  const lockedPaths   = editMode === 'troubleshoot' ? getLockedPaths(changedPaths) : new Set();

  // Fetch suggestions and optimiser results from backend when params change
  useEffect(() => {
    if (!params) { setSuggestions([]); setOptimiseResult([]); return; }
    api.post('/api/v1/parameters/suggestions', { params })
      .then(r => setSuggestions(r.data))
      .catch(() => setSuggestions([]));
    if (versions.length > 0) {
      setOptimiseResult(null);
      api.post('/api/v1/parameters/optimise', { params, versions, machine })
        .then(r => setOptimiseResult(r.data))
        .catch(() => setOptimiseResult([]));
    }
  }, [params, versions, machine]); // eslint-disable-line

  function startEdit(mode) {
    if (!baseParams) return;
    const init = {};
    SECTIONS.forEach(sec => sec.params.forEach(p => {
      const v = getPath(baseParams, p.path);
      if (v !== undefined && v !== null) init[p.path] = String(v);
    }));
    setEditValues(init); setEditReason(''); setEditOutcome(''); setSaveError('');
    setEditMode(mode);
  }
  function cancelEdit() {
    setEditMode(null); setEditValues({}); setEditReason(''); setSaveError('');
  }

  async function saveVersion() {
    if (!editReason.trim()) { setSaveError('Reason is required'); return; }
    if (editMode === 'troubleshoot' && changedGroups > 1) {
      setSaveError(`Troubleshoot: max 1 parameter group — you have ${changedGroups}`); return;
    }
    if (changedPaths.length === 0) { setSaveError('No parameters changed'); return; }
    setSaving(true); setSaveError('');
    try {
      await api.post(`/api/v1/optimisations/${id}/versions`, {
        params:         applyOverrides(baseParams, editValues),
        reason:         editReason.trim(),
        type:           editMode,
        outcome:        editOutcome || null,
        created_by:     user?.email || 'unknown',
        changed_fields: changedPaths,
      });
      cancelEdit(); load();
    } catch { setSaveError('Failed to save — try again'); }
    finally   { setSaving(false); }
  }

  async function markGolden() {
    if (!currentV) return;
    setMarkingGolden(true);
    try { await api.put(`/api/v1/optimisations/${id}/versions/${currentV.id}/golden`); load(); }
    catch { toast.error('Failed to mark golden run'); }
    finally { setMarkingGolden(false); }
  }

  async function unmarkGolden() {
    if (!goldenV) return;
    setMarkingGolden(true);
    try { await api.delete(`/api/v1/optimisations/${id}/versions/${goldenV.id}/golden`); load(); }
    catch { toast.error('Failed to unmark golden run'); }
    finally { setMarkingGolden(false); }
  }

  async function applySuggestions(selectedKeys) {
    if (!params || !selectedKeys.length) return;
    setSuggestApplying(true);
    try {
      const allSugs = suggestions;
      let newParams = JSON.parse(JSON.stringify(params));
      const allChanged = [];
      allSugs.filter(sg => sg.fixable && selectedKeys.includes(sg.key)).forEach(sg => {
        sg.changes.forEach(ch => {
          newParams = setPath(newParams, ch.path, ch.after);
          allChanged.push(ch.path);
        });
      });
      const labels = allSugs.filter(sg => selectedKeys.includes(sg.key)).map(sg => sg.label).join('; ');
      await api.post(`/api/v1/optimisations/${id}/versions`, {
        params:         newParams,
        reason:         `Suggest Fixes: ${labels}`,
        type:           'suggest_fixes',
        created_by:     user?.email || 'unknown',
        changed_fields: allChanged,
      });
      setShowSuggest(false);
      setSuggestSel([]);
      load();
    } catch { toast.error('Failed to apply suggestions — try again'); }
    finally  { setSuggestApplying(false); }
  }

  const [recalculating, setRecalculating] = useState(false);
  const [recalcError, setRecalcError]     = useState('');

  async function recalculate() {
    setRecalculating(true); setRecalcError('');
    try {
      const [machineRes, mouldRes, materialRes] = await Promise.all([
        api.get(`/api/v1/machines/${opt.machine_id}`),
        api.get(`/api/v1/moulds/${opt.mould_id}`),
        api.get(`/api/v1/materials/${opt.recipe.material_id}`),
      ]);
      const calcRes = await api.post('/api/v1/parameters/calculate', {
        machine:  machineRes.data,
        mould:    mouldRes.data,
        material: materialRes.data,
      });
      if (calcRes.data.error) { setRecalcError(calcRes.data.error); return; }
      await api.post(`/api/v1/optimisations/${id}/versions`, {
        params:     calcRes.data,
        type:       'calculated',
        reason:     'Recalculated — machine/mould data updated',
        created_by: user?.email || 'system',
      });
      load();
    } catch (err) {
      setRecalcError(err.response?.data?.error || 'Recalculation failed');
    } finally {
      setRecalculating(false);
    }
  }

  async function applyOptimise(selectedKeys) {
    if (!params || !selectedKeys.length) return;
    setOptimiseApplying(true);
    try {
      let newParams = JSON.parse(JSON.stringify(params));
      const allChanged = [];
      (optimiseResult || []).filter(sg => sg.fixable && selectedKeys.includes(sg.key)).forEach(sg => {
        sg.changes.forEach(ch => {
          newParams = setPath(newParams, ch.path, ch.after);
          allChanged.push(ch.path);
        });
      });
      const labels = (optimiseResult || []).filter(sg => selectedKeys.includes(sg.key)).map(sg => sg.label).join('; ');
      await api.post(`/api/v1/optimisations/${id}/versions`, {
        params:         newParams,
        reason:         `Optimise: ${labels}`,
        type:           'optimise',
        created_by:     user?.email || 'unknown',
        changed_fields: allChanged,
      });
      setShowOptimise(false);
      setOptimiseSel([]);
      load();
    } catch { toast.error('Failed to apply optimisation — try again'); }
    finally  { setOptimiseApplying(false); }
  }

  if (loading) return <div style={s.center}>Loading…</div>;
  if (!opt)    return <div style={s.center}>Optimisation not found</div>;

  const machine = opt.machine;
  const mould   = opt.mould;
  const recipe  = opt.recipe;
  const isGoldenCurrent = currentV && goldenV && currentV.id === goldenV.id;
  const nextVer = (latestV?.version_number || 0) + 1;

  // ── EDIT MODE: compact 3-column ────────────────────────────
  if (editMode) {
    const showCurrent = refV?.id !== currentV?.id;
    return (
      <div style={s.page}>
        <div style={s.editModeHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ ...s.editModeBadge, background: editMode === 'troubleshoot' ? '#7c2d12' : '#3b0764' }}>
              {editMode === 'troubleshoot' ? '🔍 Troubleshoot' : '✨ Optimise'} — V{nextVer}
            </span>
            {editMode === 'troubleshoot' && (
              <span style={{ fontSize: '12px', fontWeight: '600', color: changedGroups > 1 ? '#ef4444' : changedGroups === 1 ? '#22c55e' : '#6b7280' }}>
                {changedGroups}/1 group changed
              </span>
            )}
            {editMode === 'edit' && changedPaths.length > 0 && (
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>{changedPaths.length} changed</span>
            )}
          </div>
          <button style={s.cancelBtn} onClick={cancelEdit}>✕ Cancel</button>
        </div>

        {/* ── Comparison table ─────────────────────────── */}
        {(() => {
          const cols = showCurrent ? '160px 1fr 1fr 1fr' : '160px 1fr 1fr';
          const thStyle = { padding: '10px 14px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6b7280', borderBottom: '2px solid #2a3040', background: '#161b22' };
          const cellBase = (locked, changed) => ({
            padding: '8px 14px', borderBottom: '1px solid #252f40',
            display: 'flex', alignItems: 'center',
            background: changed ? 'rgba(20,184,166,0.04)' : 'transparent',
            opacity: locked ? 0.55 : 1,
          });
          return (
            <div style={{ display: 'grid', gridTemplateColumns: cols, background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', overflow: 'hidden', marginBottom: '80px' }}>
              {/* Column headers */}
              <div style={{ ...thStyle, color: '#374151' }} />
              <div style={{ ...thStyle, color: goldenV ? '#f59e0b' : '#14b8a6' }}>
                {goldenV ? '⭐ Golden Run' : '🔬 V1 Calculated'}
                <span style={{ fontWeight: '400', marginLeft: '6px', textTransform: 'none', letterSpacing: 0 }}>
                  V{refV?.version_number} · {refV ? new Date(refV.created_at).toLocaleDateString() : ''}
                </span>
              </div>
              {showCurrent && (
                <div style={{ ...thStyle, color: '#f0f2f5' }}>
                  {TYPE_ICONS[currentV?.type] || '✏️'} V{currentV?.version_number}
                  <span style={{ fontWeight: '400', marginLeft: '6px', textTransform: 'none', letterSpacing: 0, color: '#6b7280' }}>
                    {currentV ? new Date(currentV.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              )}
              <div style={{ ...thStyle, color: '#14b8a6' }}>
                {editMode === 'troubleshoot' ? '🔍' : '✨'} V{nextVer} — editing
              </div>

              {/* Parameter rows grouped by section */}
              {SECTIONS.map(sec => {
                const rows = sec.params.filter(p => getPath(baseParams, p.path) != null);
                if (!rows.length) return null;
                return (
                  <Fragment key={sec.key}>
                    <div style={{ gridColumn: '1 / -1', padding: '6px 14px', fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#14b8a6', background: '#161b22', borderBottom: '1px solid #2a3040', borderTop: '1px solid #2a3040' }}>
                      {sec.label}
                    </div>
                    {rows.map(p => {
                      const isLocked  = lockedPaths.has(p.path);
                      const isChanged = changedPaths.includes(p.path);
                      const refVal    = refV?.params != null ? getPath(refV.params, p.path) : undefined;
                      const curVal    = showCurrent && currentV?.params != null ? getPath(currentV.params, p.path) : undefined;
                      const editVal   = editValues[p.path] !== undefined ? editValues[p.path] : String(getPath(baseParams, p.path) ?? '');
                      const curVsRef  = curVal != null && refVal != null && String(curVal) !== String(refVal);
                      const editVsRef = !isLocked && refVal !== undefined && parseFloat(editVal) !== parseFloat(refVal);
                      return (
                        <Fragment key={p.path}>
                          {/* Label */}
                          <div style={{ ...cellBase(isLocked, isChanged), color: '#6b7280', fontSize: '12px', gap: '6px' }}>
                            {p.label}
                            {p.unit && <span style={{ fontSize: '10px', color: '#374151' }}>{p.unit}</span>}
                          </div>
                          {/* Reference value */}
                          <div style={{ ...cellBase(isLocked, false), fontFamily: 'monospace', fontSize: '12px', fontWeight: '600', color: '#9ca3af' }}>
                            {refVal ?? '—'}
                          </div>
                          {/* Current version value */}
                          {showCurrent && (
                            <div style={{ ...cellBase(isLocked, false), fontFamily: 'monospace', fontSize: '12px', fontWeight: '600', color: curVsRef ? '#14b8a6' : '#f0f2f5' }}>
                              {curVal ?? '—'}
                            </div>
                          )}
                          {/* Edit input */}
                          <div style={cellBase(isLocked, isChanged)}>
                            <input
                              disabled={isLocked}
                              style={{ width: '72px', background: isLocked ? '#0f1117' : isChanged ? '#0d1f2d' : '#0f1117', border: `1px solid ${isLocked ? '#2a3040' : isChanged ? '#14b8a6' : '#2a3040'}`, borderRadius: '4px', color: isLocked ? '#6b7280' : editVsRef ? '#14b8a6' : '#f0f2f5', padding: '4px 8px', fontSize: '12px', fontFamily: 'monospace', textAlign: 'right', cursor: isLocked ? 'not-allowed' : 'text' }}
                              value={editVal}
                              onChange={e => !isLocked && setEditValues(prev => ({ ...prev, [p.path]: e.target.value }))}
                            />
                            {p.unit && <span style={{ fontSize: '10px', color: isLocked ? '#2a3040' : '#4b5563', marginLeft: '4px' }}>{p.unit}</span>}
                          </div>
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
            </div>
          );
        })()}

        <div style={s.saveFooter}>
          <div style={s.saveRow}>
            <select style={s.saveSelect} value={editOutcome} onChange={e => setEditOutcome(e.target.value)}>
              <option value="">Outcome (optional)</option>
              <option value="adjusted">Adjusted</option>
              <option value="stable">Stable</option>
              <option value="issue">Issue</option>
            </select>
            <input
              style={s.saveInput}
              placeholder="Reason for change (required)"
              value={editReason}
              onChange={e => setEditReason(e.target.value)}
            />
            {saveError && <span style={{ fontSize: '11px', color: '#ef4444', flexShrink: 0 }}>{saveError}</span>}
            <button style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={saveVersion}>
              {saving ? 'Saving…' : `Save V${nextVer}`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── NORMAL MODE: rich view ──────────────────────────────────
  const checks      = params?.checks || {};
  const anyFail     = Object.values(checks).some(c => c.pass === false);
  const hasRedFail = Object.entries(checks).some(([k, c]) => c.pass === false && CHECK_DETAILS[k]?.severity === 'red');
  const bannerBg   = !anyFail ? '#14532d' : hasRedFail ? '#7f1d1d' : '#2d1f00';
  const bannerBdr  = !anyFail ? '#166534' : hasRedFail ? '#991b1b' : '#92400e';

  const steps = [
    { key: 'recipe',        label: 'Recipe' },
    { key: 'checks',        label: 'Safety Checks' },
    { key: 'temps',         label: 'Temperatures' },
    { key: 'injection',     label: 'Injection' },
    { key: 'packing',       label: 'Packing' },
    { key: 'cooling',       label: 'Cooling' },
    { key: 'plasticising',  label: 'Plasticising' },
    { key: 'clamp',         label: 'Clamp Force' },
    { key: 'closing',       label: 'Mould Closing' },
    { key: 'opening',       label: 'Mould Opening' },
    { key: 'ejection',      label: 'Ejection' },
    { key: 'notes',         label: 'Notes & Cycle' },
    { key: 'machine_check', label: 'Machine Checklist' },
    { key: 'mould_check',   label: 'Mould Checklist' },
    { key: 'fai',           label: 'FAI Checklist' },
  ];

  // JSX elements built from current params (stable component refs = state preserved)
  const RecipeEl = (
    <div style={s.section}>
      <div style={s.sectionTitle}>Recipe — {recipe?.name}</div>
      {recipe?.description && <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>{recipe.description}</div>}
      <PRow label="Base material" value={recipe?.material?.name} color="#14b8a6" />
      {(recipe?.recipe_components || []).map(c => (
        <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1a2030' }}>
          <span style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', background: c.type === 'masterbatch' ? '#1d4ed8' : c.type === 'base' ? '#0f766e' : '#166534', color: '#fff' }}>
              {c.type === 'masterbatch' ? 'MB' : c.type === 'base' ? 'BASE' : 'ADD'}
            </span>
            {c.material?.name}
          </span>
          <span style={{ fontSize: '13px', fontWeight: '700', fontFamily: 'monospace' }}>{c.percentage}%</span>
        </div>
      ))}
      {(!recipe?.recipe_components || recipe.recipe_components.length === 0) && (
        <div style={{ fontSize: '11px', color: '#6b7280', paddingTop: '4px' }}>Base material only</div>
      )}
    </div>
  );

  const ChecksEl = (
    <div style={s.section}>
      <div style={{ background: bannerBg, border: `1px solid ${bannerBdr}`, borderRadius: '10px', padding: '14px 18px', marginBottom: '12px' }}>
        <span style={{ fontSize: '16px', fontWeight: '700' }}>
          {anyFail ? '⚠ Issues found — review checks below' : '✓ All checks passed — ready to set up'}
        </span>
      </div>
      <div style={s.checksGrid}>
        {Object.entries(checks).map(([key, check]) => {
          const detail = CHECK_DETAILS[key] || {};
          const isExp  = expandedCheck === key;
          const sev    = detail.severity || 'amber';
          return (
            <div key={key} onClick={() => setExpandedCheck(isExp ? null : key)}
              style={{ ...s.checkCard, cursor: 'pointer',
                background: check.pass === false ? (sev === 'red' ? '#3b0f0f' : '#2d1f00') : '#0f2d1f',
                border: `1px solid ${check.pass === false ? (sev === 'red' ? '#991b1b' : '#92400e') : '#166534'}`,
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px', color: check.pass === false ? (sev === 'red' ? '#ef4444' : '#f59e0b') : '#22c55e' }}>
                  {check.pass === true ? '✓' : check.pass === false ? (sev === 'red' ? '✗' : '⚠') : '—'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', fontWeight: '700' }}>{detail.label || key.replace(/_/g, ' ')}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{check.value}</div>
                </div>
                <span style={{ fontSize: '10px', color: '#6b7280' }}>{isExp ? '▲' : '▼'}</span>
              </div>
              {isExp && (
                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #2a3040' }}>
                  {check.pass === false ? (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: sev === 'red' ? '#fca5a5' : '#fde68a', marginBottom: '4px' }}>
                        {sev === 'red' ? '🔴 Requires mechanical/physical change' : '🟡 Fixable by changing controller settings'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#d1d5db', lineHeight: '1.5' }}>{detail.fix}</div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '11px', color: '#86efac' }}>✓ This check passed — no action required.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const TempsEl = (
    <Sec title="Barrel Zone Temperatures" info={INFO.zones}>
      <div style={s.zoneRow}>
        {(params?.zones || []).map((z, i) => (
          <div key={i} style={s.zoneCard}>
            <div style={s.zoneLabel}>{z.label}</div>
            <div style={{ fontWeight: '700', fontFamily: 'monospace', color: '#f59e0b', fontSize: `${fontMult * 1.4}em` }}>{z.temp}°C</div>
          </div>
        ))}
      </div>
      <PRow label="Mould temperature" value={`${params?.mould_temp}°C`} info={INFO.mould_temp} color="#14b8a6" />
      <PRow label="Estimated melt temperature" value={`${params?.melt_temp_estimated}°C`} info={INFO.melt_temp}
        color={checks?.melt_in_window?.pass ? '#22c55e' : '#ef4444'}
        range={params?.melt_temp_window ? `Processing window: ${params.melt_temp_window.min}–${params.melt_temp_window.max}°C` : undefined} />
    </Sec>
  );

  const InjEl = (
    <Sec title="Injection" info={INFO.inj_pressure}>
      <PRow label="Fill time"              value={`${params?.fill_time_s}s`}         info={INFO.fill_time} />
      <PRow label="Max injection pressure" value={`${params?.inj_pressure_bar} bar`} info={INFO.inj_pressure} color="#14b8a6" range={machine?.max_inj_bar ? `Machine max: ${machine.max_inj_bar} bar` : undefined} />
      <PRow label="Shot volume"            value={`${params?.shot_vol_cm3} cm³`}     info={INFO.shot_vol} />
      <PRow label="Dosing stroke"          value={`${params?.dosing_stroke_mm} mm`}  info={INFO.dosing_stroke} range={machine?.barrel_cm3 ? `Barrel: ${machine.barrel_cm3} cm³` : undefined} />
      <PRow label={`V→P switchover (${params?.vp_type})`} value={params?.vp_value}  info={INFO.vp} color="#14b8a6" />
      {params?.shear_rate && (
        <PRow label="Gate shear rate" value={`${params.shear_rate.toLocaleString()} s⁻¹`} info={INFO.shear_rate}
          color={checks?.gate_shear_rate?.pass ? '#22c55e' : '#ef4444'}
          range={params.shear_limit ? `Material max: ${params.shear_limit.toLocaleString()} s⁻¹` : undefined} />
      )}
      <StageTbl headers={['#', 'Speed %', 'Pressure bar', 'Until mm']} info={INFO.inj_stages}
        rows={(params?.inj_stages || []).map(st => [st.stage, st.speed_pct, st.pressure_bar, st.until_mm])} />
    </Sec>
  );

  const PackEl = (
    <Sec title="Packing" info={INFO.gate_freeze}>
      <PRow label="Gate freeze time" value={`${params?.gate_freeze_s}s`} info={INFO.gate_freeze} />
      <PRow label="Total hold time"  value={`${params?.hold_total_s}s`}  info={INFO.hold_stages} color="#14b8a6" />
      <PRow label="Suckback"         value={`${params?.suckback_mm} mm`} info={INFO.suckback} />
      <StageTbl headers={['#', 'Pressure bar', 'Time s']} info={INFO.hold_stages}
        rows={(params?.hold_stages || []).map(st => [st.stage, st.pressure_bar, st.time_s])} />
    </Sec>
  );

  const CoolEl = (
    <Sec title="Cooling" info={INFO.cooling_min}>
      <PRow label="Minimum cooling time"     value={params?.cooling_min_s ? `${params.cooling_min_s}s` : '—'} info={INFO.cooling_min} />
      <PRow label="Recommended cooling time" value={params?.cooling_rec_s ? `${params.cooling_rec_s}s` : '—'} info={INFO.cooling_rec} color="#14b8a6" />
      <PRow label="Flow rate per circuit"    value={`${params?.flow_rate_l_min} L/min`}                       info={INFO.flow_rate} />
    </Sec>
  );

  const PlastEl = (
    <Sec title="Plasticising" info={INFO.screw_speed}>
      <PRow label="Screw speed"   value={`${params?.screw_speed_rpm} rpm`}   info={INFO.screw_speed}    color="#14b8a6" range={params?.screw_speed_overlap   ? `Machine × material: ${params.screw_speed_overlap} rpm`   : undefined} />
      <PRow label="Back pressure" value={`${params?.back_pressure_bar} bar`} info={INFO.back_pressure}  color="#14b8a6" range={params?.back_pressure_overlap ? `Machine × material: ${params.back_pressure_overlap} bar` : undefined} />
      <PRow label="Recovery time" value={`${params?.recovery_time_s}s`}      info={INFO.recovery}       color={checks?.recovery_vs_cooling?.pass ? '#22c55e' : '#f59e0b'} />
      <PRow label="Shot / barrel" value={`${params?.shot_barrel_ratio}%`}    info={INFO.shot_barrel}    color={checks?.shot_barrel_ratio?.pass ? '#22c55e' : '#f59e0b'} range="Ideal range: 20–80%" />
      {params?.residence_time_s && (
        <PRow label="Residence time" value={`${params.residence_time_s}s`} info={INFO.residence}
          color={checks?.residence_time?.pass ? '#22c55e' : '#ef4444'} range="Max: 360s" />
      )}
    </Sec>
  );

  const ClampEl = (
    <Sec title="Clamp Force" info={INFO.clamp}>
      <PRow label="Minimum clamp force"   value={params?.clamp_min_kn   ? `${params.clamp_min_kn} kN`   : '—'} info={INFO.clamp} />
      <PRow label="Suggested clamp force" value={params?.clamp_force_kn ? `${params.clamp_force_kn} kN` : '—'} info={INFO.clamp_sugg} color="#14b8a6" />
    </Sec>
  );

  const ClosingEl = (
    <Sec title="Mould Closing" info={INFO.closing}>
      <StageTbl headers={['#', 'Pressure bar', 'Speed mm/s', 'Until mm', 'Note']} info={INFO.closing}
        rows={(params?.closing_stages || []).map(st => [st.stage, st.pressure_bar, st.speed_mm_s, st.until_mm, st.note])} />
    </Sec>
  );

  const OpeningEl = (
    <Sec title="Mould Opening" info={INFO.opening}>
      <StageTbl headers={['#', 'Pressure bar', 'Speed mm/s', 'Until mm', 'Note']} info={INFO.opening}
        rows={(params?.opening_stages || []).map(st => [st.stage, st.pressure_bar, st.speed_mm_s, st.until_mm, st.note])} />
    </Sec>
  );

  const EjectEl = (
    <Sec title="Ejection" info={INFO.ejection}>
      <PRow label="Delay after open" value={`${params?.ejector_delay_s}s`}     info={INFO.ejection} />
      <PRow label="Ejector stroke"   value={`${params?.ejector_stroke_mm} mm`} info={INFO.ej_stroke} />
      <PRow label="Forward speed"    value={`${params?.ejector_speed} mm/s`}   info={INFO.ej_fwd} color="#14b8a6" />
      <PRow label="Retract speed"    value={`${params?.ejector_retract} mm/s`} info={INFO.ej_ret} />
      <PRow label="Pulses"           value={params?.ejector_pulses}             info={INFO.ej_pulses} />
    </Sec>
  );

  const NotesEl = (
    <div>
      <div style={s.section}>
        <div style={s.sectionTitle}>Cycle Summary</div>
        <CycleWaterfall result={params} fontMult={fontMult} />
      </div>
      {(machine?.machine_notes || mould?.mould_notes) && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Notes</div>
          {machine?.machine_notes && (
            <div style={{ ...s.infoBox, marginBottom: '10px' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#93c5fd', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>ℹ Machine — {machine?.display_name || machine?.name}</div>
              <div style={{ fontSize: '12px', color: '#bfdbfe', lineHeight: '1.6' }}>{machine.machine_notes}</div>
            </div>
          )}
          {mould?.mould_notes && (
            <div style={s.infoBox}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#93c5fd', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>ℹ Mould — {mould.ref}</div>
              <div style={{ fontSize: '12px', color: '#bfdbfe', lineHeight: '1.6' }}>{mould.mould_notes}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const stepContents = [
    RecipeEl, ChecksEl, TempsEl, InjEl, PackEl, CoolEl, PlastEl,
    ClampEl, ClosingEl, OpeningEl, EjectEl, NotesEl,
    <Checklist type="machine" entityId={machine?.id} entityName={machine?.name} />,
    <Checklist type="mould"   entityId={mould?.id}   entityName={`${mould?.ref} — ${mould?.name}`} />,
    <Checklist type="fai"     entityId={recipe?.id}  entityName={recipe?.name} />,
  ];

  return (
    <div style={{ ...s.page, fontSize: `${fontMult}em` }}>

      {/* ── Header ─────────────────────────────────────────── */}
      <div style={s.resultsHeader}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button style={s.backBtn} onClick={() => navigate('/process')}>← Process</button>
          {canEdit && <>
          <button
            style={{ ...s.backBtn, color: recalculating ? '#6b7280' : '#14b8a6', borderColor: '#14b8a6', opacity: recalculating ? 0.6 : 1 }}
            disabled={recalculating}
            onClick={recalculate}
          >
            {recalculating ? '🔄 Recalculating…' : '🔄 Recalculate'}
          </button>
          <button
            style={{ ...s.goldenBtn, background: isGoldenCurrent ? '#166534' : '#92400e', border: `1px solid ${isGoldenCurrent ? '#15803d' : '#b45309'}` }}
            onClick={isGoldenCurrent ? unmarkGolden : markGolden}
            disabled={markingGolden}
          >
            {isGoldenCurrent ? '⭐ Unmark Golden Run' : '⭐ Mark as Golden Run'}
          </button>
          <button
            style={optimiseResult?.length > 0
              ? { ...s.editBtn, background: '#1e3a5f', color: '#93c5fd', border: '1px solid #1d4ed8', animation: 'optimise-glow 1.8s ease-in-out infinite' }
              : s.editBtn}
            onClick={() => {
              if (optimiseResult?.length > 0) { setOptimiseSel(optimiseResult.filter(sg => sg.fixable).map(sg => sg.key)); setShowOptimise(true); }
              else startEdit('edit');
            }}
          >
            {optimiseResult?.length > 0 ? '✨ Optimise' : optimiseResult === null ? '✨ Optimise…' : '✨ Optimise'}
          </button>
          <button style={s.troubleshootBtn} onClick={() => startEdit('troubleshoot')}>🔍 Troubleshoot</button>
          <button
            style={{ ...s.suggestBtn, opacity: suggestions.length === 0 ? 0.45 : 1 }}
            disabled={suggestions.length === 0}
            onClick={() => { setSuggestSel(suggestions.filter(sg => sg.fixable).map(sg => sg.key)); setShowSuggest(true); }}
          >
            🔧 Suggest Fixes{suggestions.length > 0 ? ` (${suggestions.length})` : ''}
          </button>
          </>}
          <button
            style={{ ...s.histBtn, ...(showHistory ? s.histBtnActive : {}) }}
            onClick={() => setShowHistory(h => !h)}
          >
            📋 History ({versions.length})
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={s.viewToggle}>
            <button style={{ ...s.viewBtn, ...(viewMode === 'full'  ? s.viewBtnActive : {}) }} onClick={() => setViewMode('full')}>Full view</button>
            <button style={{ ...s.viewBtn, ...(viewMode === 'steps' ? s.viewBtnActive : {}) }} onClick={() => { setViewMode('steps'); setCurrentStep(0); }}>Step by step</button>
          </div>
          <div style={s.fontControls}>
            <span style={s.fontLabel}>A:</span>
            <button style={{ ...s.fontBtn, ...(fontKey === 'normal' ? s.fontBtnActive : {}) }} onClick={() => { setFontKey('normal'); document.body.style.zoom = '100%'; }}>A</button>
            <button style={{ ...s.fontBtn, ...(fontKey === 'large'  ? s.fontBtnActive : {}) }} onClick={() => { setFontKey('large');  document.body.style.zoom = '125%'; }}>A+</button>
            <button style={{ ...s.fontBtn, ...(fontKey === 'xl'     ? s.fontBtnActive : {}) }} onClick={() => { setFontKey('xl');     document.body.style.zoom = '150%'; }}>A++</button>
          </div>
        </div>
      </div>

      {recalcError && (
        <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '10px' }}>
          Recalculation failed: {recalcError}
        </div>
      )}

      {/* ── Breadcrumb / version ─────────────────────────── */}
      <div style={s.breadcrumb}>
        <span style={s.bcItem}>{machine?.name}</span>
        <span style={s.bcArrow}>→</span>
        <span style={s.bcItem}>{mould?.ref} — {mould?.name}</span>
        <span style={s.bcArrow}>→</span>
        <span style={s.bcItem}>{recipe?.name}</span>
        {currentV && (
          <>
            <span style={s.bcArrow}>·</span>
            <span style={{ fontSize: '11px', color: isGoldenCurrent ? '#f59e0b' : '#6b7280' }}>
              {viewingVersion ? `Viewing V${currentV.version_number}` : `V${currentV.version_number} (latest)`}
              {isGoldenCurrent && ' ⭐'}
            </span>
            {viewingVersion && (
              <button style={{ fontSize: '10px', color: '#14b8a6', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', marginLeft: '4px' }}
                onClick={() => setViewingVersion(null)}>reset to latest</button>
            )}
          </>
        )}
      </div>

      {/* ── Version history panel ─────────────────────────── */}
      {showHistory && (
        <div style={s.historyPanel}>
          <div style={s.historyTitle}>Version History</div>
          {[...versions].reverse().map(v => {
            const outcome   = OUTCOME_LABELS[v.outcome];
            const isViewing = viewingVersion === v.id;
            return (
              <div key={v.id} style={{ ...s.histRow, ...(isViewing ? { background: '#0d1f2d' } : {}) }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', minWidth: 0 }}>
                  <span style={s.histBadge}>V{v.version_number}</span>
                  <span style={{ fontSize: '13px', flexShrink: 0 }}>{TYPE_ICONS[v.type] || '✏️'}</span>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '2px' }}>{v.reason || '—'}</div>
                    <div style={{ fontSize: '10px', color: '#6b7280' }}>
                      {new Date(v.created_at).toLocaleDateString()}
                      {v.created_by  ? ` · ${v.created_by}` : ''}
                      {v.changed_fields?.length ? ` · ${v.changed_fields.length} param${v.changed_fields.length !== 1 ? 's' : ''} changed` : ''}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  {outcome && <span style={{ ...s.outcomeBadge, background: outcome.bg, color: outcome.color }}>{outcome.label}</span>}
                  {v.is_golden && <span>⭐</span>}
                  <button
                    style={{ ...s.viewVerBtn, ...(isViewing ? { background: '#14b8a6', border: '1px solid #14b8a6', color: '#fff' } : {}) }}
                    onClick={() => { setViewingVersion(isViewing ? null : v.id); setShowHistory(false); }}
                  >
                    {isViewing ? 'Reset' : 'View'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── SUGGEST FIXES PANEL ──────────────────────────── */}
      {showSuggest && (
        <SuggestFixesPanel
          suggestions={suggestions}
          selected={suggestSel}
          onToggle={key => setSuggestSel(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])}
          onApply={() => applySuggestions(suggestSel)}
          applying={suggestApplying}
          onClose={() => setShowSuggest(false)}
        />
      )}

      {/* ── OPTIMISE PANEL ───────────────────────────────── */}
      {showOptimise && (
        <OptimisePanel
          suggestions={optimiseResult || []}
          selected={optimiseSel}
          onToggle={key => setOptimiseSel(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])}
          onApply={() => applyOptimise(optimiseSel)}
          applying={optimiseApplying}
          onClose={() => setShowOptimise(false)}
        />
      )}

      {/* ── FULL VIEW ─────────────────────────────────────── */}
      {viewMode === 'full' && (
        <div>
          <div style={s.legend}>
            <span style={s.legendTitle}>Colours:</span>
            <LegItem color="#f59e0b" label="Zone temp" />
            <LegItem color="#14b8a6" label="Recommended" />
            <LegItem color="#22c55e" label="Good range" />
            <LegItem color="#ef4444" label="Critical" />
            <LegItem color="#f0f2f5" label="Neutral" />
          </div>
          {RecipeEl}
          {ChecksEl}
          {TempsEl}
          {InjEl}
          {PackEl}
          {CoolEl}
          {PlastEl}
          {ClampEl}
          {ClosingEl}
          {OpeningEl}
          {EjectEl}
          {NotesEl}
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap', marginBottom: '40px' }}>
            <button style={s.checklistBtn} onClick={() => setShowChecklist('machine')}>📋 Machine Checklist</button>
            <button style={s.checklistBtn} onClick={() => setShowChecklist('mould')}>📋 Mould Checklist</button>
            <button style={s.checklistBtn} onClick={() => setShowChecklist('fai')}>📋 FAI Checklist</button>
          </div>
          {showChecklist && (
            <div style={s.overlay} onClick={() => setShowChecklist(null)}>
              <div style={s.modal} onClick={e => e.stopPropagation()}>
                <div style={s.modalHeader}>
                  <div style={s.modalTitle}>
                    {showChecklist === 'machine' ? `Machine Checklist — ${machine?.name}` :
                     showChecklist === 'mould'   ? `Mould Checklist — ${mould?.ref}` :
                     `FAI Checklist — ${recipe?.name}`}
                  </div>
                  <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '18px', cursor: 'pointer' }} onClick={() => setShowChecklist(null)}>✕</button>
                </div>
                <div style={{ padding: '16px 20px' }}>
                  {showChecklist === 'machine' && <Checklist type="machine" entityId={machine?.id} entityName={machine?.name} />}
                  {showChecklist === 'mould'   && <Checklist type="mould"   entityId={mould?.id}   entityName={`${mould?.ref} — ${mould?.name}`} />}
                  {showChecklist === 'fai'     && <Checklist type="fai"     entityId={recipe?.id}  entityName={recipe?.name} />}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── STEP BY STEP VIEW ─────────────────────────────── */}
      {viewMode === 'steps' && (
        <div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '16px', fontWeight: '700' }}>{steps[currentStep].label}</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{currentStep + 1} / {steps.length}</span>
            </div>
            <div style={{ height: '4px', background: '#2a3040', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ height: '100%', background: '#14b8a6', borderRadius: '2px', transition: 'width 0.3s ease', width: `${((currentStep + 1) / steps.length) * 100}%` }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {steps.map((step, i) => (
              <button key={step.key}
                style={{ background: i === currentStep ? '#14b8a6' : '#1e2329', border: `1px solid ${i === currentStep ? '#14b8a6' : '#2a3040'}`, color: i === currentStep ? '#fff' : '#6b7280', borderRadius: '6px', padding: '4px 10px', fontSize: '10px', fontWeight: '700', cursor: 'pointer' }}
                onClick={() => setCurrentStep(i)}>
                {step.label}
              </button>
            ))}
          </div>
          <div style={{ minHeight: '300px' }}>
            {stepContents[currentStep]}
          </div>
          <div style={{ height: '144px' }} />
          <div style={s.stepFooter}>
            <button style={s.stepFooterBtn} disabled={currentStep === 0} onClick={() => setCurrentStep(c => Math.max(0, c - 1))}>← Previous</button>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>{currentStep + 1} / {steps.length}</span>
            <button style={{ ...s.stepFooterBtn, background: '#14b8a6', color: '#fff' }}
              onClick={() => currentStep === steps.length - 1 ? setViewMode('full') : setCurrentStep(c => c + 1)}>
              {currentStep === steps.length - 1 ? 'Go to full view' : 'Next →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared rich-view sub-components ──────────────────────────

function Sec({ title, info, children }) {
  const [show, setShow] = useState(false);
  return (
    <div style={s.section}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <div style={s.sectionTitle}>{title}</div>
        {info && <span style={s.infoIcon} onClick={() => setShow(v => !v)}>ℹ</span>}
      </div>
      {show && info && <div style={s.infoBox}>{info}</div>}
      {children}
    </div>
  );
}

function PRow({ label, value, info, color, range }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ ...s.paramRow, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={s.paramLabel}>{label}</span>
        {info && <span style={s.infoIcon} onClick={() => setShow(v => !v)}>ℹ</span>}
        {show && <div style={s.tooltip}>{info}{range ? ` · ${range}` : ''}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {range && !show && <span style={{ fontSize: '10px', color: '#4b5563' }}>{range}</span>}
        <span style={{ fontSize: '13px', fontWeight: '700', fontFamily: 'monospace', color: color || '#f0f2f5' }}>{value}</span>
      </div>
    </div>
  );
}

function StageTbl({ headers, rows, info }) {
  const [show, setShow] = useState(false);
  const cols     = headers.length;
  const gridCols = cols === 4 ? '30px 1fr 1fr 1fr' : '30px 1fr 1fr 1fr 2fr';
  return (
    <div style={{ marginTop: '10px' }}>
      {info && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          <span style={{ fontSize: '11px', color: '#6b7280' }}>Stages</span>
          <span style={s.infoIcon} onClick={() => setShow(v => !v)}>ℹ</span>
        </div>
      )}
      {show && <div style={s.infoBox}>{info}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '8px', padding: '6px 8px', background: '#0f1117', borderRadius: '6px 6px 0 0', fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.06em' }}>
        {headers.map((h, i) => <span key={i}>{h}</span>)}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '8px', padding: '8px', borderTop: '1px solid #2a3040', fontSize: '12px', fontFamily: 'monospace' }}>
          {row.map((cell, j) => (
            <span key={j} style={j === row.length - 1 && cols === 5 ? { color: '#6b7280', fontSize: '10px' } : {}}>{cell}</span>
          ))}
        </div>
      ))}
    </div>
  );
}

function LegItem({ color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: color, flexShrink: 0 }} />
      <span style={{ fontSize: '10px', color: '#9ca3af' }}>{label}</span>
    </div>
  );
}

function CycleWaterfall({ result, fontMult = 1 }) {
  const [expanded, setExpanded] = useState(null);
  if (!result) return null;

  const segs = [
    { key: 'close',   label: 'Close',    color: '#6366f1',
      time: result.closing_stages ? result.closing_stages.reduce((acc, st) => acc + (st.until_mm / Math.max(st.speed_mm_s, 1)), 0).toFixed(1) : '—',
      detail: `${result.closing_stages?.length || 0} stages — fast close, slow approach, high-pressure lock` },
    { key: 'fill',    label: 'Fill',     color: '#3b82f6',
      time: result.fill_time_s,
      detail: `${result.inj_stages?.length || 0} injection stages at up to ${result.inj_pressure_bar} bar` },
    { key: 'hold',    label: 'Hold',     color: '#8b5cf6',
      time: result.hold_total_s,
      detail: `${result.hold_stages?.length || 0} hold stages — gate freeze: ${result.gate_freeze_s}s` },
    { key: 'cool',    label: 'Cool',     color: '#14b8a6',
      time: result.cooling_rec_s,
      detail: `Min: ${result.cooling_min_s}s — Recommended: ${result.cooling_rec_s}s` },
    { key: 'recover', label: 'Recovery', color: '#f59e0b',
      time: result.recovery_time_s,
      detail: `Screw recovery at ${result.screw_speed_rpm} rpm — ${result.shot_barrel_ratio}% of barrel` },
    { key: 'open',    label: 'Open',     color: '#6366f1',
      time: result.opening_stages ? result.opening_stages.reduce((acc, st) => acc + (st.until_mm / Math.max(st.speed_mm_s, 1)), 0).toFixed(1) : '—',
      detail: `${result.opening_stages?.length || 0} stages — slow break, fast open, decelerate` },
    { key: 'eject',   label: 'Eject',    color: '#ec4899',
      time: result.ejector_stroke_mm && result.ejector_speed
        ? ((result.ejector_stroke_mm / result.ejector_speed) * 2 * result.ejector_pulses + result.ejector_delay_s).toFixed(1)
        : '—',
      detail: `Delay: ${result.ejector_delay_s}s — ${result.ejector_pulses} pulse(s) at ${result.ejector_speed} mm/s` },
  ];

  const total = segs.reduce((sum, seg) => sum + (parseFloat(seg.time) || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', height: '52px', marginBottom: '8px' }}>
        {segs.map(seg => {
          const t   = parseFloat(seg.time) || 0;
          const pct = total > 0 ? (t / total) * 100 : 0;
          return (
            <div key={seg.key} onClick={() => setExpanded(expanded === seg.key ? null : seg.key)}
              style={{ flex: Math.max(pct, 3), background: seg.color, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'flex 0.3s', minWidth: '30px', overflow: 'hidden' }}>
              <div style={{ fontSize: '9px', fontWeight: '700', color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{seg.label}</div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#fff' }}>{String(seg.time).replace('.', ',')}s</div>
            </div>
          );
        })}
      </div>
      {expanded && (
        <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '8px', padding: '10px 14px', marginBottom: '8px', fontSize: '12px', color: '#9ca3af' }}>
          <strong style={{ color: '#f0f2f5' }}>{segs.find(seg => seg.key === expanded)?.label}:</strong>{' '}
          {segs.find(seg => seg.key === expanded)?.detail}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>Estimated total cycle time</span>
        <span style={{ fontSize: `${fontMult * 1.1}em`, fontWeight: '700', color: '#14b8a6' }}>{total.toFixed(1).replace('.', ',')}s</span>
      </div>
      <div style={{ fontSize: '10px', color: '#4b5563', marginTop: '2px' }}>Click any segment to see details. Mould movement times are estimated from speed and stroke data.</div>
    </div>
  );
}

// ── Suggest Fixes panel ───────────────────────────────────────
function SuggestFixesPanel({ suggestions, selected, onToggle, onApply, applying, onClose }) {
  const fixableSelected = selected.filter(k => suggestions.find(sg => sg.key === k && sg.fixable));
  return (
    <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '15px', fontWeight: '700', color: '#f0f2f5' }}>🔧 Suggest Fixes</div>
        <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '18px', cursor: 'pointer' }} onClick={onClose}>✕</button>
      </div>
      {suggestions.length === 0 && (
        <div style={{ fontSize: '13px', color: '#6b7280' }}>No issues found — all safety checks passed.</div>
      )}
      {suggestions.map(sg => (
        <div key={sg.key} style={{ background: sg.fixable ? '#161b22' : '#111418', border: `1px solid ${sg.fixable ? '#2a3040' : '#1a2030'}`, borderRadius: '8px', padding: '14px', marginBottom: '10px', opacity: sg.fixable ? 1 : 0.7 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            {sg.fixable ? (
              <input type="checkbox" checked={selected.includes(sg.key)} onChange={() => onToggle(sg.key)}
                style={{ marginTop: '2px', accentColor: '#14b8a6', flexShrink: 0 }} />
            ) : (
              <span style={{ fontSize: '16px', flexShrink: 0 }}>🔴</span>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: sg.fixable ? '#f0f2f5' : '#9ca3af', marginBottom: '4px' }}>{sg.label}</div>
              <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.5', marginBottom: sg.changes.length ? '10px' : 0 }}>{sg.description}</div>
              {sg.changes.length > 0 && selected.includes(sg.key) && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '6px' }}>
                  {sg.changes.map((ch, i) => (
                    <div key={i} style={{ background: '#0d1f2d', border: '1px solid #1d4ed8', borderRadius: '6px', padding: '6px 10px', fontSize: '11px' }}>
                      <div style={{ color: '#6b7280', marginBottom: '2px' }}>{ch.label}</div>
                      <div style={{ color: '#9ca3af' }}>
                        <span style={{ fontFamily: 'monospace' }}>{ch.before}</span>
                        <span style={{ color: '#4b5563', margin: '0 6px' }}>→</span>
                        <span style={{ fontFamily: 'monospace', color: '#14b8a6', fontWeight: '700' }}>{ch.after}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!sg.fixable && (
                <div style={{ fontSize: '10px', color: '#f59e0b', marginTop: '4px' }}>Requires physical/mechanical change — cannot be auto-applied</div>
              )}
            </div>
          </div>
        </div>
      ))}
      {fixableSelected.length > 0 && (
        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            style={{ background: '#0d9488', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', opacity: applying ? 0.6 : 1 }}
            disabled={applying}
            onClick={onApply}
          >
            {applying ? 'Applying…' : `Apply ${fixableSelected.length} fix${fixableSelected.length !== 1 ? 'es' : ''} as new version`}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Optimise panel ────────────────────────────────────────────
function OptimisePanel({ suggestions, selected, onToggle, onApply, applying, onClose }) {
  const fixableSelected = selected.filter(k => suggestions.find(sg => sg.key === k && sg.fixable));
  return (
    <div style={{ background: '#0d1f3a', border: '1px solid #1d4ed8', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <div style={{ fontSize: '15px', fontWeight: '700', color: '#93c5fd' }}>✨ Optimise</div>
        <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '18px', cursor: 'pointer' }} onClick={onClose}>✕</button>
      </div>
      <div style={{ fontSize: '11px', color: '#4b6fa8', marginBottom: '16px' }}>
        Calculated from version history, physics checks, and parameter trends. Select which improvements to apply.
      </div>
      {suggestions.length === 0 && (
        <div style={{ fontSize: '13px', color: '#6b7280' }}>No further improvements found — process looks well optimised.</div>
      )}
      {suggestions.map(sg => (
        <div key={sg.key} style={{ background: '#0a1628', border: '1px solid #1e3a5f', borderRadius: '8px', padding: '14px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <input type="checkbox" checked={selected.includes(sg.key)} onChange={() => onToggle(sg.key)}
              style={{ marginTop: '2px', accentColor: '#3b82f6', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#bfdbfe', marginBottom: '4px' }}>{sg.label}</div>
              <div style={{ fontSize: '11px', color: '#4b6fa8', lineHeight: '1.5', marginBottom: sg.changes.length && selected.includes(sg.key) ? '10px' : 0 }}>{sg.description}</div>
              {sg.changes.length > 0 && selected.includes(sg.key) && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '6px' }}>
                  {sg.changes.map((ch, i) => (
                    <div key={i} style={{ background: '#0d1f3a', border: '1px solid #1e3a5f', borderRadius: '6px', padding: '6px 10px', fontSize: '11px' }}>
                      <div style={{ color: '#4b6fa8', marginBottom: '2px' }}>{ch.label}</div>
                      <div>
                        <span style={{ fontFamily: 'monospace', color: '#6b7280' }}>{ch.before}</span>
                        <span style={{ color: '#374151', margin: '0 6px' }}>→</span>
                        <span style={{ fontFamily: 'monospace', color: '#3b82f6', fontWeight: '700' }}>{ch.after}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      {fixableSelected.length > 0 && (
        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', opacity: applying ? 0.6 : 1 }}
            disabled={applying}
            onClick={onApply}
          >
            {applying ? 'Applying…' : `Apply ${fixableSelected.length} optimisation${fixableSelected.length !== 1 ? 's' : ''} as new version`}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────
const s = {
  page:         { ...cs.page, paddingBottom: '60px' },
  center:       cs.center,

  resultsHeader:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' },
  backBtn:      cs.btnSecondary,
  goldenBtn:    { color: '#fde68a', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  editBtn:      { background: C.surface, color: '#c4b5fd', border: '1px solid #4c1d95', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  troubleshootBtn: { background: C.surface, color: '#fdba74', border: '1px solid #92400e', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  suggestBtn:      { background: C.surface, color: '#86efac', border: '1px solid #14532d', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  histBtn:      { background: C.surface, color: '#9ca3af', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 14px', fontSize: '12px', cursor: 'pointer' },
  histBtnActive:{ background: C.border, color: C.text },
  viewToggle:   { display: 'flex', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden' },
  viewBtn:      { background: 'none', border: 'none', color: C.muted, padding: '6px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  viewBtnActive:{ background: C.teal, color: '#fff' },
  fontControls: { display: 'flex', alignItems: 'center', gap: '6px' },
  fontLabel:    { fontSize: '11px', color: C.muted },
  fontBtn:      { background: C.surface, border: `1px solid ${C.border}`, color: C.muted, borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' },
  fontBtnActive:{ background: C.teal, color: '#fff', border: `1px solid ${C.teal}` },

  breadcrumb:   { display: 'flex', alignItems: 'center', gap: '8px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', flexWrap: 'wrap' },
  bcItem:       { fontSize: '12px', fontWeight: '600', color: C.teal },
  bcArrow:      { color: C.mutedDark },

  legend:       { display: 'flex', alignItems: 'center', gap: '16px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', flexWrap: 'wrap' },
  legendTitle:  { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.06em' },

  section:      { ...cs.card, marginBottom: '12px' },
  sectionTitle: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.teal },
  checksGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' },
  checkCard:    { padding: '12px', borderRadius: '8px' },
  zoneRow:      { display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' },
  zoneCard:     { background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', flex: 1, minWidth: '80px', textAlign: 'center' },
  zoneLabel:    { fontSize: '9px', color: C.muted, marginBottom: '4px' },
  paramRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` },
  paramLabel:   { fontSize: '12px', color: C.muted },
  infoIcon:     { fontSize: '11px', color: '#3b82f6', cursor: 'pointer', userSelect: 'none', background: '#1d2d4a', border: '1px solid #1d4ed8', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  infoBox:      { background: '#1e2d45', border: '1px solid #1d4ed8', borderRadius: '8px', padding: '10px 12px', fontSize: '11px', color: '#93c5fd', lineHeight: '1.6', marginBottom: '10px' },
  tooltip:      { position: 'absolute', left: 0, top: '100%', zIndex: 50, background: '#1e2d45', border: '1px solid #1d4ed8', borderRadius: '8px', padding: '8px 12px', fontSize: '11px', color: '#93c5fd', lineHeight: '1.5', maxWidth: '300px', whiteSpace: 'normal' },

  checklistBtn: { background: C.surface, border: `1px solid ${C.border}`, color: '#9ca3af', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', cursor: 'pointer', flex: 1, minWidth: '160px' },
  overlay:      cs.overlay,
  modal:        { ...cs.modal, maxWidth: '500px' },
  modalHeader:  cs.modalHeader,
  modalTitle:   cs.modalTitle,

  historyPanel: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', marginBottom: '16px', overflow: 'hidden' },
  historyTitle: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.teal, padding: '12px 14px', borderBottom: `1px solid ${C.border}` },
  histRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #1a2030', gap: '8px' },
  histBadge:    { fontSize: '10px', fontWeight: '700', background: C.border, color: '#9ca3af', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 },
  outcomeBadge: { fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 },
  viewVerBtn:   { background: 'none', border: `1px solid ${C.border}`, color: '#9ca3af', borderRadius: '5px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', flexShrink: 0 },

  stepFooter:    { position: 'fixed', bottom: '64px', left: 0, right: 0, background: C.surface, borderTop: `1px solid ${C.border}`, padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 150 },
  stepFooterBtn: cs.btnSecondary,

  // Edit mode
  editModeHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', gap: '12px', flexWrap: 'wrap' },
  editModeBadge:  { fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '4px', color: C.text },
  cancelBtn:      { background: 'none', border: '1px solid #374151', color: '#9ca3af', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', flexShrink: 0 },
  saveFooter:     { position: 'fixed', bottom: 0, left: 0, right: 0, background: C.surfaceAlt, borderTop: `1px solid ${C.border}`, padding: '12px 16px', zIndex: 50 },
  saveRow:        { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', maxWidth: '900px', margin: '0 auto' },
  saveSelect:     { background: C.surface, border: `1px solid ${C.border}`, color: '#9ca3af', borderRadius: '6px', padding: '8px 10px', fontSize: '12px', flexShrink: 0 },
  saveInput:      { flex: 1, minWidth: '200px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', color: C.text, padding: '8px 10px', fontSize: '12px', fontFamily: 'system-ui' },
  saveBtn:        { ...cs.btnPrimary, padding: '9px 18px', flexShrink: 0 },
};
